import { useRef, type ReactNode, type ChangeEvent, type FocusEvent, type KeyboardEvent, type ReactEventHandler } from 'react';
import { Box, IconButton, TextareaAutosize, Tooltip } from '@mui/material';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { useOptionalEditableDocument } from './EditableDocumentContext';

export interface EditableFieldTextProps {
  fieldId: string;
  /** The field's raw (unresolved) value — authors edit the `{{token}}` source, not the display text. */
  value: string;
  onCommit: (nextValue: string) => void;
  onFocusLabel?: string;
  fontSize?: number;
  /** The element's own text look, so the edit box shows what the toolbar just set. */
  align?: 'left' | 'center' | 'right';
  italic?: boolean;
  uppercase?: boolean;
  placeholder?: string;
  /** The element's formatting toolbar; it positions itself, so it adds no height to the field. */
  toolbar?: ReactNode;
  testId: string;
}

/**
 * Inline canvas text editing for a selected field (LLAMS-610 Part 7). Renders only from
 * `SelectableElement` once an element is selected — never in read-only preview, never in the PDF.
 *
 * Registers with `EditableDocumentContext`'s own caret-insert bridge on focus, so `CanvasValuePicker`
 * (a `blocks/presentation` component, which may import this `document-render` module — the accepted
 * `blocks → document-render` direction `LivePreview.tsx` already uses) can insert `{{scope.path}}`
 * here without `document-render` importing anything back from `blocks/`.
 *
 * Known simplification (reported in the PR): the field's raw `{{token}}` text renders as plain
 * characters here, not as the peach-tinted inline chip Figma `be.header.editor` shows — that visual
 * treatment is left for a follow-up polish pass; insertion and editing both work correctly today.
 */
export function EditableFieldText({
  fieldId,
  value,
  onCommit,
  onFocusLabel,
  fontSize,
  align,
  italic,
  uppercase,
  placeholder,
  toolbar,
  testId
}: EditableFieldTextProps) {
  const editable = useOptionalEditableDocument();
  const selectionRef = useRef({ start: value.length, end: value.length });
  const elementRef = useRef<HTMLTextAreaElement | null>(null);
  const insertButtonRef = useRef<HTMLButtonElement | null>(null);
  // The registered caret-insert handler below is created once, when focus fires, but the field
  // keeps being edited (and re-rendering with a new `value` prop) for as long as it stays focused
  // — a closure over `value` itself would insert against the text as it was AT FOCUS TIME, not the
  // current text (found live: typing after focusing, then inserting, silently dropped the typed
  // text). Read the live value through a ref instead, exactly like `useValueFieldCaretInsert.ts`.
  const valueRef = useRef(value);
  valueRef.current = value;

  const trackSelection: ReactEventHandler<HTMLTextAreaElement> = (event) => {
    selectionRef.current = {
      start: event.currentTarget.selectionStart ?? 0,
      end: event.currentTarget.selectionEnd ?? 0
    };
  };

  const handleFocus = () => {
    editable?.registerCaretInsertHandler((path: string) => {
      const token = `{{${path}}}`;
      const { start, end } = selectionRef.current;
      const current = valueRef.current;
      const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
      const nextCaret = start + token.length;
      selectionRef.current = { start: nextCaret, end: nextCaret };
      onCommit(next);
      elementRef.current?.focus();
    });
  };

  const handleBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    // A MOUSE click on the insert-value button never reaches here at all (its own `onMouseDown`
    // below calls `preventDefault`, so the textarea never blurs). A KEYBOARD user tabbing from the
    // textarea to that button fires a genuine native blur first, with `relatedTarget` set to the
    // button it's moving to — clearing the handler here would race ahead of the pick and turn
    // `insertAtCaret` into a silent no-op (found live: keyboard-only insertion never landed). Only
    // clear when focus is moving somewhere genuinely unrelated to this field's own insert affordance.
    if (event.relatedTarget && event.relatedTarget === insertButtonRef.current) return;
    // The same goes for focus moving into the open value picker (opening it focuses its search
    // field): the pick it is about to make is this field's insert.
    if (event.relatedTarget && editable?.valuePickerAnchor) return;
    editable?.registerCaretInsertHandler(null);
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    onCommit(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      elementRef.current?.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      elementRef.current?.blur();
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {toolbar}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--size-4, 4px)', width: '100%' }}>
        <TextareaAutosize
          ref={elementRef}
          value={value}
          placeholder={placeholder}
          aria-label={onFocusLabel ?? 'Edit field text'}
          data-testid={testId}
          data-field-editing={fieldId}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onSelect={trackSelection}
          onClick={trackSelection}
          onKeyUp={trackSelection}
          onKeyDown={handleKeyDown}
          style={{
            flex: 1,
            width: '100%',
            minWidth: 0,
            textAlign: align ?? 'inherit',
            fontStyle: italic ? 'italic' : undefined,
            // Not over a `{{token}}`: the box edits the token's source, which is case-sensitive.
            textTransform: uppercase && !value.includes('{{') ? 'uppercase' : undefined,
            resize: 'none',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            // A textarea does not inherit its text color; left alone it takes the host theme's (white in dark mode) onto the white sheet.
            color: 'inherit',
            font: 'inherit',
            fontSize,
            padding: 0,
            margin: 0
          }}
        />
        {editable ? (
          <Tooltip title="Insert a dynamic value">
            <IconButton
              ref={insertButtonRef}
              size="small"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => insertButtonRef.current && editable.requestValuePicker(insertButtonRef.current)}
              aria-label="Insert a dynamic value"
              data-testid={`${testId}:insert-variable`}
              sx={{ width: 'var(--size-20, 20px)', height: 'var(--size-20, 20px)', color: 'var(--icon-neutral)' }}
            >
              <DataObjectIcon sx={{ fontSize: 'var(--size-14, 14px)' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
}
